import { memo, useMemo } from "react";
import { Marker } from "@react-google-maps/api";
import { RAINFALL_UNIT } from "core/features/maps/rainfall-interpolation";
import { white } from "core/resources/theme/colors";

export function formatStationPrecipitacaoLabel(
  value: unknown,
  fallback?: string
): string {
  if (value === null || value === undefined || value === "") {
    if (fallback) {
      return fallback.includes(RAINFALL_UNIT)
        ? fallback
        : `${fallback} ${RAINFALL_UNIT}`;
    }
    return `0 ${RAINFALL_UNIT}`;
  }

  const numeric =
    typeof value === "number" ? value : Number(String(value).replace(",", "."));

  if (!Number.isFinite(numeric)) {
    return `${value} ${RAINFALL_UNIT}`;
  }

  const formatted = Number.isInteger(numeric)
    ? String(numeric)
    : numeric.toFixed(1).replace(/\.0$/, "");

  return `${formatted} ${RAINFALL_UNIT}`;
}

/** Parse station mm for display decisions (0 / invalid → hide Chuvas pin). */
export function getStationPrecipitacaoMm(value: unknown): number | null {
  if (value === null || value === undefined || value === "") {
    return 0;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value.replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Chuvas pins with 0 mm clutter the map (often stacked duplicates).
 * Keep 0 in IDW samples; hide only the visual pin/label.
 */
export function shouldShowChuvasStationPin(value: unknown): boolean {
  const mm = getStationPrecipitacaoMm(value);
  return mm != null && mm > 0;
}

function escapeSvgText(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function getChuvasPinIcon(text: string): google.maps.Icon | string {
  if (typeof google === "undefined") {
    return "/indicacao-meteorologica.svg";
  }

  const width = Math.max(54, Math.ceil(text.length * 6.5 + 20));
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="20" viewBox="0 0 ${width} 20">
    <circle cx="6" cy="10" r="4.5" fill="#1b5e20" stroke="#fff" stroke-width="1.5"/>
    <text x="14" y="13.5" fill="#e60000" stroke="#fff" stroke-width="2.5" paint-order="stroke" stroke-linejoin="round" font-family="Arial,sans-serif" font-size="11" font-weight="800">${escapeSvgText(
      text
    )}</text>
  </svg>`;
  const size = new google.maps.Size(width, 20);
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    size,
    scaledSize: size,
    anchor: new google.maps.Point(6, 10),
  };
}

export interface ChuvasStationMarkerProps {
  markerKey: string;
  lat: number;
  lng: number;
  precipitacao: unknown;
  visible: boolean;
  isChuvasStyle: boolean;
  precipitacaoFallback?: string;
}

/**
 * Native Google Marker (not OverlayView) so zoom/pan stay responsive.
 * OverlayView redraws every frame and freezes the Chuvas map with many stations.
 */
function ChuvasStationMarkerComponent({
  lat,
  lng,
  precipitacao,
  visible,
  isChuvasStyle,
  precipitacaoFallback,
}: ChuvasStationMarkerProps) {
  const position = useMemo(() => ({ lat, lng }), [lat, lng]);
  const text = formatStationPrecipitacaoLabel(
    precipitacao,
    precipitacaoFallback
  );

  const show =
    visible && (!isChuvasStyle || shouldShowChuvasStationPin(precipitacao));

  const icon = useMemo(
    () =>
      isChuvasStyle
        ? getChuvasPinIcon(text)
        : "/indicacao-meteorologica.svg",
    [isChuvasStyle, text]
  );

  const label = useMemo(() => {
    if (isChuvasStyle) {
      return undefined;
    }
    return {
      text,
      color: white,
      fontWeight: "800",
      className: "pluviometrico-value",
    };
  }, [isChuvasStyle, text]);

  return (
    <Marker
      visible={show}
      zIndex={1000}
      icon={icon}
      position={position}
      label={label}
      options={{ optimized: true }}
    />
  );
}

export const ChuvasStationMarker = memo(ChuvasStationMarkerComponent);
